
import { UploadFileType } from "./upload-file.dto";

export enum FILE_TRANSFER_STATUS {
    CREATED = 'CREATED',
    TRANSFERRING = 'TRANSFERRING',
    UNKNOWN = 'UNKNOWN',
}

export interface ICreateFileTransferRequest {
    uploadedFileUuid: string;
    destinationPath: string;
    executeCommand: string;
    permissions: string;
    description: string;
}
export type TUpdateFileTransferRequest = Omit<ICreateFileTransferRequest, 'uploadedFileUuid'>;

export interface ICreateFileTransferResponse {
    fileTransfer: FileTransferDto;
}

export interface IGetFileTransfersResponse {
    fileTransfers: FileTransferDto[];
}

export interface IUpdateFileTransferResponse {
    updated: boolean;
}

export interface IDeleteFileTransferResponse {
    deleted: boolean;
}

export class FileTransferDto {
    constructor(private readonly _object: unknown) {
    }
    get uuid(): string {
        return this._object['uuid'] || '';
    }
    get uploadedFileUuid(): string {
        return this._object['uploadedFileUuid'] || '';
    }
    get status(): FILE_TRANSFER_STATUS {
        if (this._object['status'] === FILE_TRANSFER_STATUS.CREATED || this._object['status'] === FILE_TRANSFER_STATUS.TRANSFERRING) {
            return this._object['status'];
        }
        return FILE_TRANSFER_STATUS.UNKNOWN;
    }
    get fileName(): string {
        return this._object['fileName'] || '';
    }
    get version(): string {
        return this._object['version'] || '';
    }
    get destinationPath(): string {
        return this._object['destinationPath'] || '';
    }
    get executeCommand(): string {
        return this._object['executeCommand'] || '';
    }
    get permissions(): string {
        return this._object['permissions'] || '';
    }
    get description(): string {
        return this._object['description'] || '';
    }
    get awsArn(): string {
        return this._object['awsArn'] || '';
    }
    get updatedAt(): Date {
        return this._object['updatedAt'] ? new Date(this._object['updatedAt']) : null;
    }
    get createdAt(): Date {
        return this._object['createdAt'] ? new Date(this._object['createdAt']) : null;
    }
    get type(): UploadFileType {
        if (!this._object['type'] || !Object.values(UploadFileType).includes(this._object['type'])) {
            return UploadFileType.USER;
        }
        return this._object['type'];
    }
}

class OutOfServiceTimeRange {
    startTime: string;
    endTime: string;
}
class JobConfigurations {
    description?: string;
    timeoutInMinutes?: number;
}

export class CreateApiFileTransferExecution {
    overrideOutOfServiceWindows?: boolean;
    outOfServiceWindows?: OutOfServiceTimeRange[];
    fileUuids: string[];
    requestReboot?: boolean;
    requestScript?: string;
    jobConfigurations?: JobConfigurations;
};

export class FileTranferExecutionDto {
    private static convertOutOfServiceWindows(outOfServiceWindows: any[]): any[] {
        return outOfServiceWindows.map((outOfServiceWindow) => ({
            startTime: outOfServiceWindow?.['start_time'],
            endTime: outOfServiceWindow?.['end_time'],
        }))
    }
    constructor(private readonly _object: unknown) {
    }

    get jobId(): string {
        return this._object['jobId'] || '';
    }

    get status(): string {
        return this._object['status'] || '';
    }

    get description(): string {
        return this._object['description'] || '';
    }

    get files(): Omit<FileTransferDto, 'createdAt' | 'updatedAt' | 'status'>[] {
        return this._object['files'] || this._object['document']?.files || [];
    }

    get requestReboot(): boolean {
        return this._object['document']?.['request_reboot'] || false;
    }

    get outOfServiceWindows(): OutOfServiceTimeRange[] {
        return FileTranferExecutionDto.convertOutOfServiceWindows(this._object['document']?.['out_of_services'] || []);
    }

    get createdAt(): Date {
        return this._object['createdAt'] ? new Date(this._object['createdAt']) : null;
    }

    get updatedAt(): Date {
        return this._object['updatedAt'] ? new Date(this._object['updatedAt']) : null;
    }
}

export class TransferredFileDto {
    constructor(private readonly _object: unknown) {
    }

    get filePath(): string {
        return this._object['filePath'] || '';
    }

    get version(): string {
        return this._object['version'] || '';
    }

    get status(): string {
        return this._object['status'] || '';
    }

    get updatedAt(): Date {
        return this._object['updatedAt'] ? new Date(this._object['updatedAt']) : null;
    }
}

export interface IFTScriptResult {
    error?: string;
    exit_code?: number;
    output?: string;
}
export interface IFileTransferResult {
    error?: string;
    file_path?: string;
    post_script_result?: IFTScriptResult;
}
export class DeviceFileTransferExecutionResultDto {
    constructor(private readonly _object: unknown) { }

    get key(): string {
        return `${this.job_id}-${this.createdAtTimestamp}`;
    }

    get job_id(): string {
        return this._object['job_id'] || '';
    }

    get status(): string {
        return this._object['status'] || '';
    }

    get start_script_result(): IFTScriptResult {
        return this._object['start_script_result'] || {};
    }

    get end_script_result(): IFTScriptResult {
        return this._object['end_script_result'] || {};
    }

    get file_results(): IFileTransferResult[] {
        return this._object['file_results'] || [];
    }

    get removal_results(): IFileTransferResult[] {
        return this._object['removal_results'] || [];
    }

    get createdAtTimestamp(): number {
        return this.createdAt?.getTime();
    }

    get createdAt(): Date {
        return this._object['timestamp'] ? new Date(this._object['timestamp']) : null;
    }

    toJson() {
        return this._object;
    }
}